// src/components/payment/ExtraContribution.tsx
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, Heart } from "lucide-react";
import { PAYMENT_CONFIG } from "../../config/payment.config";

interface ExtraContributionProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
}

export const ExtraContribution: React.FC<ExtraContributionProps> = ({
  value,
  onChange,
  className = "",
}) => {
  // Set to true by default so it's expanded
  const [isExpanded, setIsExpanded] = useState(true);

  const increment = () => {
    const newValue = value + PAYMENT_CONFIG.extraContributionStep;
    if (newValue <= PAYMENT_CONFIG.maxExtraContribution) {
      onChange(newValue);
    }
  };

  const decrement = () => {
    const newValue = value - PAYMENT_CONFIG.extraContributionStep;
    if (newValue >= 0) {
      onChange(newValue);
    }
  };

  const quickAmounts = [500, 1000, 2000, 5000];

  return (
    <div
      className={`bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 ${className}`}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Heart size={16} className="text-primary" />
          <span className="text-sm font-medium text-white">Support LRNSY</span>
        </div>
        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <Plus size={16} className="text-white/60" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="text-xs text-text-tertiary mt-3 mb-4">
              Help us create more love moments. Add a little extra to your platform fee (minimum{" "}
              {Math.trunc(PAYMENT_CONFIG.basePlatformFee + PAYMENT_CONFIG.basePlatformFee * 0.16)}{" "}
              KES).
            </p>

            {/* Quick amount buttons */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => onChange(amount)}
                  className="px-2 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium text-white transition-colors"
                >
                  +{amount}
                </button>
              ))}
            </div>

            {/* Custom amount controls */}
            <div className="flex items-center gap-3 bg-black/30 rounded-xl p-2">
              <button
                onClick={decrement}
                disabled={value <= 0}
                className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Minus size={14} />
              </button>

              <div className="flex-1 text-center">
                <span className="text-lg font-bold text-white">+ {value} KES</span>
              </div>

              <button
                onClick={increment}
                disabled={value >= PAYMENT_CONFIG.maxExtraContribution}
                className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={14} />
              </button>
            </div>

            {value > 0 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center text-[10px] text-primary/60 mt-3"
              >
                Thank you for your extra support! 💝
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
